import React, { useCallback, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import get from 'lodash.get'
import { useFormattingContext } from '../../../FormattingProvider/FormattingContext'
import ComponentBar from '../../../../charts/echarts/ComponentBar'
import { useChartContext } from '../../../../charts/echarts/ChartProvider'
import { usePipelineDataContextWithVariables } from '../../provider/DDPDataProvider'
import useReportComponentAccessors from '../../inspector/useReportComponentAccessors'
import { buildPropAccessors } from '../../inspector/buildPropAccessors'
import { interceptFormatters } from './formatInterceptor'

const useFormatters = (valueFormat, categoryFormat) => {
  const { formatter } = useFormattingContext()

  const valueFormatter = useCallback(d => formatter(d, valueFormat), [formatter, valueFormat])
  const categoryFormatter = useCallback(d => formatter(d, categoryFormat), [formatter, categoryFormat])

  return {
    valueFormatter,
    categoryFormatter,
    formatter
  }
}

function DDPComponentBar ({
  pipeline,
  variables,

  // Data structure hints
  variant = 'simple', // 'simple', 'stacked', 'grouped', 'multi-series'

  // Context-aware accessors (work differently based on variant)
  xAccessor, // Simple: item.x accessor | Multi-series: seriesItem.x accessor
  yAccessor, // Simple: item.y accessor | Multi-series: seriesItem.y accessor

  // Series accessors - consistent with other DDP visualizations
  seriesAccessor, // Simple: accessor to data array | Multi-series: accessor to series array
  seriesNameAccessor, // Accessor to name within series (e.g., "name", "label")
  seriesDataAccessor, // Accessor to data within series (e.g., "data", "values")

  // Optional data structure hints
  dataFormat, // 'array' | 'xy' | 'object' - helps interpret data structure

  // Stacking configuration (static values only)
  stack, // Stack group name (e.g., "total", "groupA")
  stackStrategy, // 'samesign' | 'all' | 'positive' | 'negative'

  // Dataset pattern support
  datasetSource, // Accessor to dataset source
  datasetDimensions, // Array of dimension names
  encode, // Static encode mapping

  // Formatting
  valueFormat = 'marketValue',
  categoryFormat = 'date',

  // All other props pass through to ComponentBar
  ...restProps
}) {
  const { valueFormatter, categoryFormatter } = useFormatters(valueFormat, categoryFormat)
  const { setSeriesLoading } = useChartContext()
  const { data, isLoading, inspectorId } = usePipelineDataContextWithVariables(pipeline, variables)

  const reportedAccessors = useMemo(
    () => buildPropAccessors({ xAccessor, yAccessor, seriesNameAccessor, seriesDataAccessor }),
    [xAccessor, yAccessor, seriesNameAccessor, seriesDataAccessor]
  )
  useReportComponentAccessors({
    inspectorId,
    component: 'DDPComponentBar',
    label: typeof pipeline === 'string' ? pipeline : null,
    accessors: reportedAccessors,
    dataAccessor: seriesAccessor
  })

  // Process data based on variant and configuration
  const processedData = useMemo(() => {
    if (!data) return null

    // Handle dataset pattern
    if (datasetSource && datasetDimensions) {
      const source = get(data, datasetSource) || data
      return {
        dataset: {
          source,
          dimensions: datasetDimensions
        },
        encode: encode || {}
      }
    }

    // Use accessor-based approach if provided
    if (variant === 'simple' && (xAccessor || yAccessor || seriesAccessor)) {
      const rawData = seriesAccessor ? get(data, seriesAccessor) : data
      const processedData = processSimpleData(rawData, { xAccessor, yAccessor, dataFormat })
      return { data: processedData }
    }

    if ((variant === 'stacked' || variant === 'multi-series' || variant === 'grouped') && seriesAccessor) {
      const seriesData = get(data, seriesAccessor) || data
      return processMultiSeriesData(seriesData, {
        seriesNameAccessor,
        seriesDataAccessor,
        xAccessor,
        yAccessor,
        stack: stack || (variant === 'stacked' ? 'total' : undefined),
        stackStrategy,
        dataFormat,
        seriesIdPrefix: restProps.seriesId || 'series'
      })
    }

    // Default: treat data as simple array
    return { data: Array.isArray(data) ? data : [] }
  }, [
    data, variant, xAccessor, yAccessor, seriesAccessor, seriesNameAccessor,
    seriesDataAccessor, dataFormat, stack,
    stackStrategy, datasetSource, datasetDimensions, encode,
    restProps.seriesId
  ])

  // Process props to intercept format strings
  const processedProps = useMemo(() => {
    return interceptFormatters({
      stack: stack || (variant === 'stacked' ? 'total' : undefined),
      stackStrategy,
      ...restProps
    }, valueFormatter, categoryFormatter)
  }, [stack, stackStrategy, restProps, valueFormatter, categoryFormatter, variant])

  // Set loading state
  useEffect(() => {
    // All data comes from a single pipeline response
    setSeriesLoading(restProps.seriesId, isLoading)
  }, [isLoading, restProps.seriesId, setSeriesLoading])

  if (isLoading) return null

  // Handle dataset pattern
  if (processedData?.dataset) {
    return (
      <ComponentBar
        dataset={processedData.dataset}
        encode={processedData.encode}
        valueFormat={valueFormatter}
        categoryFormat={categoryFormatter}
        {...processedProps}
      />
    )
  }

  // Render based on variant
  if (variant === 'simple' || !processedData?.series) {
    return (
      <ComponentBar
        data={processedData?.data || []}
        valueFormat={valueFormatter}
        categoryFormat={categoryFormatter}
        {...processedProps}
      />
    )
  }

  // Multi-series rendering
  return (
    <>
      {processedData.series.map(series => {
        // Only intercept formatters for series-specific options
        const seriesSpecificProps = series.options
          ? interceptFormatters(series.options, valueFormatter, categoryFormatter) : {}

        return (
          <ComponentBar
            key={series.id}
            seriesId={series.id}
            name={series.name}
            data={series.data}
            stack={series.stack}
            stackStrategy={series.stackStrategy}
            valueFormat={valueFormatter}
            categoryFormat={categoryFormatter}
            {...processedProps}
            {...seriesSpecificProps}
          />
        )
      })}
    </>
  )
}

// Process simple bar data
function processSimpleData (data, config) {
  if (!Array.isArray(data)) return []

  return data.map(item => {
    // Handle different data formats
    if (config.dataFormat === 'array' || Array.isArray(item)) {
      return item
    }

    if (config.dataFormat === 'xy' || (item.x !== undefined && item.y !== undefined)) {
      return [item.x, item.y]
    }

    // Use accessors
    const x = config.xAccessor ? get(item, config.xAccessor) : item[0]
    const y = config.yAccessor ? get(item, config.yAccessor) : item[1]

    return [x, y]
  })
}

// Process multi-series data
function processMultiSeriesData (seriesData, config) {
  if (!Array.isArray(seriesData)) return { series: [] }

  const {
    seriesNameAccessor,
    seriesDataAccessor,
    xAccessor,
    yAccessor,
    stack,
    stackStrategy,
    seriesIdPrefix
  } = config

  const series = seriesData.map((item, index) => {
    const name = seriesNameAccessor
      ? get(item, seriesNameAccessor)
      : (item.name || `Series ${index + 1}`)

    const rawData = seriesDataAccessor
      ? get(item, seriesDataAccessor)
      : (item.data || item)

    // Process series data items
    let processedData = rawData
    if (Array.isArray(rawData) && (xAccessor || yAccessor)) {
      processedData = rawData.map(dataItem => {
        if (Array.isArray(dataItem)) return dataItem

        const x = xAccessor ? get(dataItem, xAccessor) : dataItem.x
        const y = yAccessor ? get(dataItem, yAccessor) : dataItem.y

        return [x, y]
      })
    }

    return {
      id: `${seriesIdPrefix}_${name}_${index}`,
      name,
      data: processedData || [],
      stack,
      stackStrategy,
      options: item.options || {}
    }
  })

  return { series }
}

DDPComponentBar.propTypes = {
  // Pipeline configuration
  pipeline: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  variables: PropTypes.object,

  // Variant
  variant: PropTypes.oneOf(['simple', 'stacked', 'grouped', 'multi-series']),

  // Accessor-based data mapping
  xAccessor: PropTypes.string,
  yAccessor: PropTypes.string,
  seriesAccessor: PropTypes.string,
  seriesNameAccessor: PropTypes.string,
  seriesDataAccessor: PropTypes.string,

  // Data format hints
  dataFormat: PropTypes.oneOf(['array', 'xy', 'object']),

  // Stacking
  stack: PropTypes.string,
  stackStrategy: PropTypes.oneOf(['samesign', 'all', 'positive', 'negative']),

  // Dataset pattern
  datasetSource: PropTypes.string,
  datasetDimensions: PropTypes.arrayOf(PropTypes.string),
  encode: PropTypes.object,

  // Formatting
  valueFormat: PropTypes.string,
  categoryFormat: PropTypes.string
}

DDPComponentBar.defaultProps = {
  variant: 'simple',
  valueFormat: 'marketValue',
  categoryFormat: 'date',
  dataFormat: 'object',
  seriesAccessor: 'data',
  seriesNameAccessor: 'name',
  seriesDataAccessor: 'data',
  height: 300 // For backward compatibility with DDPComponentBar
}

export default DDPComponentBar
